import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

// Page-specific OG images mapping
const pageOgImages = {
  'home': '/og-homepage.png',
  'solutions': '/og-solutions.png',
  'features-pricing': '/og-features-pricing.png',
  'roi-calculator': '/og-roi-calculator.png',
  'ai-demo': '/og-ai-demo.png',
  'case-studies': '/og-case-studies.png',
  'about': '/og-about.png',
  'contact': '/og-contact.png',
  'book': '/og-contact.png',
  'privacy-policy': '/og-homepage.png',
  'terms-of-service': '/og-homepage.png',
  'cookie-policy': '/og-homepage.png'
};

const SEOHead = ({
  title = 'GetCare 360 | NDIS Provider Software Australia',
  description = 'AI-powered NDIS software platform for Australian disability care providers. Streamline compliance, rostering, incident management & NDIS claims.',
  keywords = 'NDIS software, NDIS provider software, disability care software, NDIS compliance, NDIS rostering, Australian NDIS, care management software',
  canonicalUrl = '',
  ogImage = null,
  ogType = 'website',
  noIndex = false,
  structuredData = null,
  page = 'home'
}) => {
  const siteUrl = 'https://getcare360.com.au';
  const fullCanonicalUrl = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;
  
  // Use page-specific OG image if not explicitly provided
  const resolvedOgImage = ogImage || pageOgImages[page] || '/og-homepage.png';
  const fullOgImage = resolvedOgImage.startsWith('http') ? resolvedOgImage : `${siteUrl}${resolvedOgImage}`;

  // Use React 19's native title support via useEffect for compatibility
  useEffect(() => {
    document.title = title;
  }, [title]);

  // Organization structured data (shown on all pages)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "GetCare 360",
    "alternateName": "GetCare360",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "description": "AI-powered NDIS software platform for Australian disability care providers",
    "foundingDate": "2013",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "AU",
      "addressRegion": "Australia"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+61-1800-GET-CARE",
      "contactType": "sales",
      "areaServed": "AU",
      "availableLanguage": "English"
    },
    "sameAs": [
      "https://www.facebook.com/getcare360",
      "https://www.linkedin.com/company/getcare360",
      "https://twitter.com/getcare360"
    ]
  };

  // Software Application schema
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "GetCare 360",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "390",
      "priceCurrency": "AUD",
      "priceValidUntil": "2026-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "127",
      "bestRating": "5",
      "worstRating": "1"
    },
    "description": "Custom-built NDIS provider management software with AI-powered compliance, rostering, and claims management.",
    "featureList": [
      "NDIS Compliance Management",
      "Participant Management",
      "Rostering & Scheduling",
      "Incident Reporting",
      "NDIS Claims Processing",
      "AI-Powered Analytics",
      "Mobile App Access",
      "Document Management"
    ]
  };

  // Local Business schema for Australian market
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "GetCare 360",
    "@id": siteUrl,
    "url": siteUrl,
    "telephone": "+61-1800-GET-CARE",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "AU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -33.8688,
      "longitude": 151.2093
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "17:00"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Australia"
    }
  };

  // Breadcrumb schema generator
  const getBreadcrumbSchema = (pageName) => {
    const breadcrumbs = {
      home: [{ name: 'Home', url: '/' }],
      solutions: [{ name: 'Home', url: '/' }, { name: 'Solutions', url: '/solutions' }],
      'features-pricing': [{ name: 'Home', url: '/' }, { name: 'Features & Pricing', url: '/features-pricing' }],
      'roi-calculator': [{ name: 'Home', url: '/' }, { name: 'ROI Calculator', url: '/roi-calculator' }],
      'ai-demo': [{ name: 'Home', url: '/' }, { name: 'AI Demo', url: '/ai-demo' }],
      'ai-features': [{ name: 'Home', url: '/' }, { name: 'AI Features', url: '/ai-features' }],
      'case-studies': [{ name: 'Home', url: '/' }, { name: 'Case Studies', url: '/case-studies' }],
      about: [{ name: 'Home', url: '/' }, { name: 'About Us', url: '/about' }],
      contact: [{ name: 'Home', url: '/' }, { name: 'Contact', url: '/contact' }],
      book: [{ name: 'Home', url: '/' }, { name: 'Book a Call', url: '/book' }],
      blog: [{ name: 'Home', url: '/' }, { name: 'Blog', url: '/blog' }],
      'privacy-policy': [{ name: 'Home', url: '/' }, { name: 'Privacy Policy', url: '/privacy-policy' }],
      'terms-of-service': [{ name: 'Home', url: '/' }, { name: 'Terms of Service', url: '/terms-of-service' }],
      'cookie-policy': [{ name: 'Home', url: '/' }, { name: 'Cookie Policy', url: '/cookie-policy' }]
    };

    const items = breadcrumbs[pageName] || breadcrumbs.home;
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": `${siteUrl}${item.url}`
      }))
    };
  };

  // Inject structured data via useEffect for React 19 compatibility
  useEffect(() => {
    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
    existingScripts.forEach(script => script.remove());

    // Combine all schemas
    const allSchemas = [
      organizationSchema,
      softwareSchema,
      localBusinessSchema,
      getBreadcrumbSchema(page),
      ...(structuredData ? [structuredData] : [])
    ];

    // Add new structured data scripts
    allSchemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', `schema-${index}`);
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // Cleanup on unmount
    return () => {
      const scripts = document.querySelectorAll('script[data-seo-jsonld]');
      scripts.forEach(script => script.remove());
    };
  }, [page, structuredData]);

  // Inject Open Graph and Twitter meta tags via useEffect for React 19 compatibility
  // react-helmet-async has issues with property attributes in React 19
  useEffect(() => {
    // Remove existing SEO meta tags
    const existingMeta = document.querySelectorAll('meta[data-seo-meta]');
    existingMeta.forEach(meta => meta.remove());
    
    // Remove existing canonical link
    const existingCanonical = document.querySelector('link[data-seo-canonical]');
    if (existingCanonical) existingCanonical.remove();

    // Define all meta tags to inject
    const metaTags = [
      // Open Graph
      { property: 'og:type', content: ogType },
      { property: 'og:url', content: fullCanonicalUrl },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: fullOgImage },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:site_name', content: 'GetCare 360' },
      { property: 'og:locale', content: 'en_AU' },
      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:url', content: fullCanonicalUrl },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: fullOgImage },
      { name: 'twitter:site', content: '@getcare360' },
    ];

    // Inject meta tags
    metaTags.forEach((tag) => {
      const meta = document.createElement('meta');
      meta.setAttribute('data-seo-meta', 'true');
      if (tag.property) {
        meta.setAttribute('property', tag.property);
      }
      if (tag.name) {
        meta.setAttribute('name', tag.name);
      }
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
    });
    
    // Inject canonical link
    const canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', fullCanonicalUrl);
    canonical.setAttribute('data-seo-canonical', 'true');
    document.head.appendChild(canonical);

    // Cleanup on unmount
    return () => {
      const metas = document.querySelectorAll('meta[data-seo-meta]');
      metas.forEach(meta => meta.remove());
      const canonicalLink = document.querySelector('link[data-seo-canonical]');
      if (canonicalLink) canonicalLink.remove();
    };
  }, [title, description, fullCanonicalUrl, fullOgImage, ogType]);

  return (
    <Helmet>
      {/* Primary Meta Tags - Skip title as it's handled via useEffect for React 19 compatibility */}
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="GetCare 360" />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Canonical link is injected via useEffect above for React 19 compatibility */}
      
      {/* Language and Region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="AU" />
      <meta name="geo.placename" content="Australia" />
      
      {/* Open Graph and Twitter meta tags are injected via useEffect above for React 19 compatibility */}
      
      {/* Additional SEO */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="GetCare 360" />
    </Helmet>
  );
};

export default SEOHead;
